/**
 * Data Plan Dialog — mirror Access popup `frmMatchingPSMainSubFormDataPlan`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubFormDataPlan.txt).
 *
 * Read-only grid xem 3 nguồn báo cáo qua 1 cbFilter combo:
 *   - Match Date - P Grade   → v_match_report_direct, match_date / p_grade  (Direct cols)
 *   - Match Date - S Grade   → v_match_report_direct, match_date / s_grade  (Direct cols)
 *   - Latest Delivery - P Grade → v_match_report_stockp, p_time / p_grade   (StockP cols)
 *   - Shipment From - S Grade  → v_match_report_stocks, s_time / s_grade    (StockS cols)
 *
 * 3 mode button (DIRECT / P-INSTRUCTION / S-INSTRUCTION) chỉ giới hạn tập
 * cbFilter option khả dụng + set default — đúng VBA btnDirect_Click /
 * btnPStock_Click / btnSStock_Click. Không có CRUD ở dialog này (Direct
 * insert/delete đi qua DirectPSDialog riêng).
 *
 * Default date range = fiscal year hiện tại (Oct 1 → Sep 30), giống Form_Load
 * Access. category mặc định trống = không lọc grade.
 */
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  useGetDataPlanRowsQuery,
  useGetMatchCategoriesQuery,
} from '../../../store/api/matchingPsApi'
import type { DataPlanFilter } from '../../../store/api/matchingPsApi'
import { useMatchingFilterPref, type DataPlanFilterCtx } from './matchingFilterPrefs'
import { matchingRangeDateFieldSx } from './matchingFilterUi'

/* ---------- formatters (đồng nhất với DirectPSDialog) --------------------- */

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function fmtDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function fmtDateTime(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

function fmtNumber(value: unknown, zeroBlank = false): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  // Access Format `#,###` (vd match_kg): số 0 → blank (khác `#,##0` ra "0").
  if (zeroBlank && n === 0) return ''
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

/**
 * Access ConditionalFormat trên control `p_side` / `s_side`
 * (form_frmMatchingPSMainSubFormDirect): chữ đỏ `#ED1C24` khi side còn pending
 * (`p_plan_pending <> 0` / `s_plan_pending <> 0`). Operator là `<>` (acNotEqual),
 * KHÔNG phải `> 0`.
 */
function hasPending(value: unknown): boolean {
  if (value == null || value === '') return false
  const n = Number(value)
  return Number.isFinite(n) && n !== 0
}

// CF cột P/S Pending: pending < 0 → chữ đỏ #ED1C24 + bold (acLessThan, value 0).
function isNegPending(value: unknown): boolean {
  if (value == null || value === '') return false
  const n = Number(value)
  return Number.isFinite(n) && n < 0
}

function cellValue(row: Record<string, unknown>, col: Col): string {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'date') return fmtDate(v)
  if (col.kind === 'datetime') return fmtDateTime(v)
  if (col.kind === 'number') return fmtNumber(v, col.zeroBlank)
  return String(v)
}

/* ---------- defaults ------------------------------------------------------ */

type DateRange = { from: string; to: string }

function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4 = now.getMonth() >= 9
  const startYear = inQ4 ? y : y - 1
  const endYear = inQ4 ? y + 1 : y
  return { from: `${startYear}-10-01`, to: `${endYear}-09-30` }
}

/* ---------- column defs --------------------------------------------------- */

type ColKind = 'text' | 'date' | 'datetime' | 'number'

interface Col {
  key: string
  label: string
  width: number
  kind?: ColKind
  align?: 'left' | 'right'
  zeroBlank?: boolean // Access Format `#,###` → số 0 hiển thị blank
}

// Mirror form_frmMatchingPSMainSubFormDirect_all — đúng thứ tự cột datasheet:
// ID, P Contract, P Supplier, P Quality, P Pending, MatchDate, MatchKG,
// S Contract, S Buyer, S Quality, S Pending, Note, P Direct, P InstructBL,
// P InstructNT, P Grade, P Quantity, S Direct, S InstructBL, S InstructNT,
// S Grade, S Quantity, Date Updated, Person Updated.
const DIRECT_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'pSide', label: 'P Contract', width: 96 },
  { key: 'pName', label: 'P Supplier', width: 126 },
  { key: 'pQuality', label: 'P Quality', width: 88 },
  { key: 'pPlanPending', label: 'P Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'matchDate', label: 'MatchDate', width: 92, kind: 'date' },
  { key: 'matchKg', label: 'MatchKG', width: 84, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'sSide', label: 'S Contract', width: 96 },
  { key: 'sName', label: 'S Buyer', width: 126 },
  { key: 'sQuality', label: 'S Quality', width: 88 },
  { key: 'sPlanPending', label: 'S Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'pDirectS', label: 'P Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'pGrade', label: 'P Grade', width: 78 },
  { key: 'pQuantity', label: 'P Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'sDirectP', label: 'S Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'sInstructBaoloc', label: 'S InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'sInstructNhontrach', label: 'S InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'sGrade', label: 'S Grade', width: 78 },
  { key: 'sQuantity', label: 'S Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

// Display order theo chị Đẹp (2026-05-27) — table-style §10.8. ID left+raw
// (match_id Long, no Format); InstructKG zero-blank (Access `#,###`).
const STOCKP_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'factoryName', label: 'StockSide', width: 116 },
  { key: 'matchDate', label: 'InstructDate', width: 110, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 96, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'pSide', label: 'P Contract', width: 96 },
  { key: 'pName', label: 'P Supplier', width: 126 },
  { key: 'pQuality', label: 'P Quality', width: 88 },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'pPlanPending', label: 'P Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'pDirectS', label: 'P Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'pGrade', label: 'P Grade', width: 78 },
  { key: 'pQuantity', label: 'P Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

// Mirror form_frmMatchingPSMainSubFormStockS_all — thứ tự cột + format condition
// verify từ doc (2026-05-27, theo yêu cầu chị Đẹp):
//   ID, StockSide, InstructDate, InstructKG, S Contract, S Buyer, S Quality, Note,
//   S Pending, S Direct, S InstructBL, S InstructNT, S Grade, S Quantity,
//   Date Updated, Person Updated.
// Access Format/TextAlign per control:
//   - match_id        TextAlign=1 (left), no Format  → left + raw (KHÔNG number-right)
//   - match_date      Format=DD-MMM-YYYY, left        → kind:'date'
//   - match_kg        Format=`#,###`, right           → number right + zeroBlank (0 → blank)
//   - s_plan_pending/s_direct_p/s_instruct_baoloc/s_instruct_nhontrach/s_quantity
//                     Format=`#,##0`, right           → number right (0 → "0")
//   - s_side          CF: chữ đỏ #ED1C24 khi [s_plan_pending]<>0 (áp ở itemContent)
//   - còn lại (s_name/s_quality/note/s_grade/factory_name/person_updated) left, raw
//   - date_updated    Text 255 (BE %Y-%m-%d %H:%M:%S) → kind:'datetime'
const STOCKS_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'factoryName', label: 'StockSide', width: 116 },
  { key: 'matchDate', label: 'InstructDate', width: 110, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 96, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'sSide', label: 'S Contract', width: 96 },
  { key: 'sName', label: 'S Buyer', width: 126 },
  { key: 'sQuality', label: 'S Quality', width: 88 },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'sPlanPending', label: 'S Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'sDirectP', label: 'S Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'sInstructBaoloc', label: 'S InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'sInstructNhontrach', label: 'S InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'sGrade', label: 'S Grade', width: 78 },
  { key: 'sQuantity', label: 'S Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

/* ---------- mode + filter mapping ----------------------------------------- */

type Mode = 'direct' | 'pstock' | 'sstock'

interface FilterOption {
  value: DataPlanFilter
  label: string
  dateLabel: string
  gradeLabel: string
  columns: Col[]
  // Schema discriminator → key cho column-visibility + column-widths (mỗi schema
  // có tập cột riêng nên persist riêng).
  schema: 'direct' | 'pstock' | 'sstock'
}

const FILTER_OPTIONS: Record<DataPlanFilter, FilterOption> = {
  match_date_p: {
    value: 'match_date_p',
    label: 'Match Date - P Grade',
    dateLabel: 'Match Date',
    gradeLabel: 'P Grade',
    columns: DIRECT_COLS,
    schema: 'direct',
  },
  match_date_s: {
    value: 'match_date_s',
    label: 'Match Date - S Grade',
    dateLabel: 'Match Date',
    gradeLabel: 'S Grade',
    columns: DIRECT_COLS,
    schema: 'direct',
  },
  latest_delivery_p: {
    value: 'latest_delivery_p',
    label: 'Latest Delivery - P Grade',
    dateLabel: 'Latest Delivery',
    gradeLabel: 'P Grade',
    columns: STOCKP_COLS,
    schema: 'pstock',
  },
  shipment_from_s: {
    value: 'shipment_from_s',
    label: 'Shipment From - S Grade',
    dateLabel: 'Shipment From',
    gradeLabel: 'S Grade',
    columns: STOCKS_COLS,
    schema: 'sstock',
  },
}

// Mỗi mode button mở 1 tập filter option + 1 default value (mirror VBA).
const MODE_CONFIG: Record<
  Mode,
  { label: string; filters: DataPlanFilter[]; defaultFilter: DataPlanFilter; color: string }
> = {
  direct: {
    label: 'DIRECT P - S',
    filters: ['match_date_p', 'match_date_s', 'latest_delivery_p', 'shipment_from_s'],
    defaultFilter: 'match_date_p',
    color: '#1e88e5',
  },
  pstock: {
    label: 'P - INSTRUCTION',
    filters: ['match_date_p', 'latest_delivery_p'],
    defaultFilter: 'latest_delivery_p',
    color: '#1e88e5',
  },
  sstock: {
    label: 'S - INSTRUCTION',
    filters: ['match_date_s', 'shipment_from_s'],
    defaultFilter: 'shipment_from_s',
    color: '#fb8c00',
  },
}

/* ---------- props --------------------------------------------------------- */

export interface DataPlanDialogProps {
  open: boolean
  onClose: () => void
}

export default function DataPlanDialog({ open, onClose }: DataPlanDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '95vh', maxHeight: '95vh' } }}
    >
      {open ? <DataPlanDialogBody onClose={onClose} /> : null}
    </Dialog>
  )
}

/* ---------- body ---------------------------------------------------------- */

function DataPlanDialogBody({ onClose }: { onClose: () => void }) {
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const [mode, setMode] = useState<Mode>('direct')
  const [filter, setFilter] = useState<DataPlanFilter>('match_date_p')
  const [range, setRange] = useState<DateRange>(fiscalDefault)
  const [category, setCategory] = useState('')

  // Persist filter (mode + cbFilter + date range + grade) qua user_preferences
  // (skill /user-logaction), context 'data-plan'. Query gate trên `hydrated` để
  // không fetch với default trước khi prefs về. Dialog re-mount mỗi lần mở →
  // hydrate lại từ prefs.
  const filterPref = useMatchingFilterPref<DataPlanFilterCtx>('data-plan')
  const { patch: patchFilter } = filterPref
  // Ref-latch để hydrate đúng 1 lần — set ref trước setState (đồng pattern
  // PurchaseContractPage) nên không trip rule react-hooks/set-state-in-effect.
  const hydratedRef = useRef(false)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    if (hydratedRef.current || filterPref.isLoading) return
    hydratedRef.current = true
    const v = filterPref.value
    const m: Mode = v.mode === 'pstock' || v.mode === 'sstock' ? v.mode : 'direct'
    const allowed = MODE_CONFIG[m].filters
    const f = (allowed as string[]).includes(v.filter)
      ? (v.filter as DataPlanFilter)
      : MODE_CONFIG[m].defaultFilter
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate-once filter từ prefs (ref-latched)
    setMode(m)
    setFilter(f)
    setRange({ from: v.dateFrom || fiscalDefault.from, to: v.dateTo || fiscalDefault.to })
    setCategory(v.category ?? '')
    setHydrated(true)
  }, [filterPref, fiscalDefault])

  // Switch mode → reset filter về default của mode đồng bộ trong cùng 1 render.
  // useEffect tách 2 render → frame trung gian Select nhận value không có trong
  // option list → MUI warning. Mirror VBA: btnDirect_Click set cbFilter ngay.
  const changeMode = useCallback(
    (next: Mode) => {
      const def = MODE_CONFIG[next].defaultFilter
      setMode(next)
      setFilter(def)
      void patchFilter({ mode: next, filter: def })
    },
    [patchFilter],
  )
  const handleFilter = useCallback(
    (v: DataPlanFilter) => {
      setFilter(v)
      void patchFilter({ filter: v })
    },
    [patchFilter],
  )
  const handleRange = useCallback(
    (next: DateRange) => {
      setRange(next)
      void patchFilter({ dateFrom: next.from || null, dateTo: next.to || null })
    },
    [patchFilter],
  )
  const handleCategory = useCallback(
    (v: string) => {
      setCategory(v)
      void patchFilter({ category: v })
    },
    [patchFilter],
  )

  const opt = FILTER_OPTIONS[filter]
  const availableFilters = MODE_CONFIG[mode].filters

  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  const rowsQuery = useGetDataPlanRowsQuery(
    {
      filter,
      startDate: range.from || undefined,
      endDate: range.to || undefined,
      category: category || undefined,
    },
    { skip: !hydrated },
  )
  const rows = useMemo(
    () => (rowsQuery.data ?? []) as Record<string, unknown>[],
    [rowsQuery.data],
  )

  const handleReload = () => {
    void rowsQuery.refetch()
  }

  /* ───────── table-style: search / per-column / sort / pagination ───────── */
  const schema = opt.schema
  const columns = opt.columns

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  // Column visibility + reorder + widths — persist per-schema qua registry
  // matching-ps (giống StockSalesDataGrid). Không cần baseline migration: subKey
  // mới fallback về `allColumns` order (= thứ tự Access đã set ở trên).
  const widthsKey = `matching.data_plan.${schema}`
  const { getWidth, startResize } = useColumnWidths(widthsKey)
  const columnsCtrl = usePreferenceColumns<Col>({
    scope: 'matching-ps',
    prefKey: 'columns',
    subKey: `data-plan-${schema}`,
    allColumns: columns,
    widthsTableKey: widthsKey,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columns, columnFilters.filters],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, columns),
    [filteredRows, sortState, columns],
  )
  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const popoverColumn = useMemo<Col | null>(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const toggleSort = (key: string) => {
    setSortState((cur) => toggleTableSort(cur, key))
    setPage(0)
  }

  const minTableWidth = Math.max(
    800,
    visibleColumns.reduce((acc, c) => acc + (c.width ?? 120), 0),
  )
  const gridKey = `${schema}:${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ pl: 2, pr: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 800, fontSize: '0.95rem' }}
        >
          DATA PLAN
        </Typography>
        <Chip
          size="small"
          label={`${rows.length.toLocaleString('en-US')} rows`}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {rowsQuery.isFetching ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : null}
        {/* 3 nút mode lên cùng hàng tiêu đề (Access btnDirect/btnPStock/btnSStock). */}
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ ml: 1 }}>
          <ModeButton
            active={mode === 'direct'}
            onClick={() => changeMode('direct')}
            icon={<SwapHorizIcon fontSize="small" />}
            label={MODE_CONFIG.direct.label}
            color={MODE_CONFIG.direct.color}
          />
          <ModeButton
            active={mode === 'pstock'}
            onClick={() => changeMode('pstock')}
            icon={<ShoppingCartOutlinedIcon fontSize="small" />}
            label={MODE_CONFIG.pstock.label}
            color={MODE_CONFIG.pstock.color}
          />
          <ModeButton
            active={mode === 'sstock'}
            onClick={() => changeMode('sstock')}
            icon={<StorefrontOutlinedIcon fontSize="small" />}
            label={MODE_CONFIG.sstock.label}
            color={MODE_CONFIG.sstock.color}
          />
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <span>
            <IconButton size="small" onClick={handleReload} disabled={rowsQuery.isFetching}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          onClick={onClose}
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<CloseIcon fontSize="small" />}
          sx={{ ml: 0.5 }}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {/* Grid — dataList template (skill table-style): FilterBar + per-column ▽
            + TableVirtuoso + Columns menu. Schema cột phụ thuộc filter chọn.
            Report filter (Choose Filter type / date / grade) đặt ở rightSlot —
            cùng hàng với Filter Area, căn phải. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <FilterBar
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
            rightSlot={
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{ justifyContent: 'flex-end' }}
              >
                <Autocomplete
                  size="small"
                  options={availableFilters}
                  value={filter}
                  onChange={(_e, v) => { if (v) handleFilter(v) }}
                  getOptionLabel={(o) => FILTER_OPTIONS[o]?.label ?? String(o)}
                  isOptionEqualToValue={(a, b) => a === b}
                  disableClearable
                  sx={{ minWidth: 210 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Choose Filter type"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.4 } }} />
                  )}
                />
                <DatePicker
                  label={opt.dateLabel}
                  format="DD-MMM-YYYY"
                  value={range.from ? dayjs(range.from) : null}
                  onChange={(d) =>
                    handleRange({ ...range, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: { shrink: true },
                      sx: matchingRangeDateFieldSx,
                    },
                  }}
                />
                <ArrowRightAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <DatePicker
                  label="to"
                  format="DD-MMM-YYYY"
                  value={range.to ? dayjs(range.to) : null}
                  onChange={(d) =>
                    handleRange({ ...range, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: { shrink: true },
                      sx: matchingRangeDateFieldSx,
                    },
                  }}
                />
                <Autocomplete
                  size="small"
                  options={categoryOptions}
                  value={category || null}
                  onChange={(_e, v) => handleCategory(v ?? '')}
                  getOptionLabel={(o) => o || ''}
                  isOptionEqualToValue={(a, b) => a === b}
                  sx={{ minWidth: 140 }}
                  renderInput={(params) => (
                    <TextField {...params} label={opt.gradeLabel}
                      placeholder="(all)"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.4 } }} />
                  )}
                />
              </Stack>
            }
          >
            <FilterButton
              label="Search"
              active={!!searchTerm.trim()}
              onClick={(e) => searchPopover.open('search', e)}
            />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openColMenu(e.currentTarget)}
              sx={filterBarBtnSx}
            >
              Columns
            </Button>
          </FilterBar>

          <Divider sx={{ mb: 0.5 }} />

          <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0 }}>
            <Box
              className="app-dataframe-toolbar"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                minHeight: 24,
                py: 0,
              }}
            >
              {(rowsQuery.isFetching || !hydrated) && <CircularProgress size={14} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
              >
                Total: {filteredRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
              >
                Loaded: {rows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={{
                  flexShrink: 0,
                  '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.68rem',
                  },
                  '& .MuiTablePagination-select': { fontSize: '0.68rem' },
                  '& .MuiTablePagination-actions': {
                    transform: 'scale(0.86)',
                    transformOrigin: 'center',
                  },
                }}
              />
            </Box>

            <FilterPopover
              open={searchPopover.state !== null}
              anchorEl={searchPopover.state?.anchor ?? null}
              onClose={() => searchPopover.close()}
              maxWidth={320}
            >
              {searchPopover.isOpen('search') && (
                <SearchFilterContent
                  value={searchTerm}
                  onChange={(v) => {
                    setSearchTerm(v)
                    setPage(0)
                  }}
                  placeholder="Contract, supplier, buyer, grade..."
                  label="Search grid"
                  onClear={() => searchPopover.close()}
                />
              )}
            </FilterPopover>

            <ColumnFilterPopover
              open={colPopover !== null}
              anchorPosition={colPopover?.position ?? null}
              onClose={() => setColPopover(null)}
              label={popoverColumn?.label ?? ''}
              kind={popoverKind === 'none' ? 'text' : popoverKind}
              value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
              onChange={(v) => {
                if (!colPopover) return
                columnFilters.setFilter(String(colPopover.key), v)
                setPage(0)
              }}
              options={popoverOptions}
            />

            <Box sx={{ flex: 1, minHeight: 280, overflow: 'hidden' }}>
              {(rowsQuery.isFetching || !hydrated) && rows.length === 0 ? (
                <BrandState tone="loading" title="Loading…" description="Please wait." />
              ) : pagedRows.length === 0 ? (
                <BrandState
                  title="No rows"
                  description="No records for the chosen filter."
                />
              ) : (
                <TableVirtuoso
                  key={gridKey}
                  style={{ height: '100%' }}
                  data={pagedRows}
                  fixedHeaderContent={() => (
                    <TableRow>
                      {visibleColumns.map((col) => {
                        const w = getWidth(String(col.key)) ?? col.width
                        return (
                          <TableCell
                            key={String(col.key)}
                            align={col.align}
                            className="app-table-header-cell"
                            onClick={() => toggleSort(String(col.key))}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              userSelect: 'none',
                              position: 'relative',
                              fontSize: '0.7rem',
                              // Padding ngang gọn (default MUI 16px/cạnh ăn hết chỗ
                              // → header dài + icon ▽ bị cắt). 6px/cạnh đủ thoáng.
                              px: 0.75,
                            }}
                          >
                            {col.label}
                            {getTableSortSuffix(sortState, String(col.key))}
                            <ColumnFilterIcon
                              active={columnFilters.isActive(String(col.key))}
                              onClick={(position) =>
                                setColPopover({ key: String(col.key), position })
                              }
                              ariaLabel={`Filter ${col.label}`}
                            />
                            <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}
                  itemContent={(_, row: Record<string, unknown>) =>
                    visibleColumns.map((col) => {
                      const val = cellValue(row, col)
                      const w = getWidth(String(col.key)) ?? col.width
                      // CF Access: P/S Contract đỏ khi side tương ứng còn pending.
                      const pendingRed =
                        (col.key === 'pSide' && hasPending(row.pPlanPending)) ||
                        (col.key === 'sSide' && hasPending(row.sPlanPending))
                      // CF cột Pending: P/S Pending < 0 → đỏ + bold (acLessThan, value 0).
                      const pendingNeg =
                        (col.key === 'pPlanPending' || col.key === 'sPlanPending') &&
                        isNegPending(row[col.key])
                      return (
                        <TableCell
                          key={String(col.key)}
                          align={col.align}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            py: 0.4,
                            px: 0.75,
                            fontSize: '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            ...(pendingRed ? { color: '#ED1C24' } : null),
                            ...(pendingNeg && { color: '#ED1C24', fontWeight: 700 }),
                          }}
                        >
                          <Box
                            component="span"
                            className="app-table-cell-text"
                            title={val || '-'}
                            sx={{
                              display: 'inline-block',
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    })
                  }
                  components={{
                    /* eslint-disable @typescript-eslint/no-explicit-any */
                    Table: (props: any) => (
                      <Table
                        {...props}
                        size="small"
                        stickyHeader
                        sx={{ minWidth: minTableWidth, width: '100%' }}
                      />
                    ),
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                    TableRow: (props: any) => <TableRow {...props} hover />,
                    /* eslint-enable @typescript-eslint/no-explicit-any */
                  }}
                />
              )}
            </Box>
          </Box>

          <FuturesColumnsMenu controller={columnsCtrl} />
        </Box>
      </DialogContent>
    </>
  )
}

/* ---------- mode button --------------------------------------------------- */

function ModeButton({
  active,
  onClick,
  icon,
  label,
  color,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  color: string
}) {
  return (
    <Button
      variant={active ? 'contained' : 'outlined'}
      size="small"
      startIcon={icon}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.75rem',
        py: 0.4,
        ...(active
          ? { bgcolor: color, color: '#fff', '&:hover': { bgcolor: color } }
          : { color, borderColor: alpha(color, 0.5) }),
      }}
    >
      {label}
    </Button>
  )
}
